import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TNewsFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import CEditor from 'src/components/editor/CEditor';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import RHFUploadDynamic from 'src/components/hook-form/RHFUploadDynamic';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { INews, useStores } from 'src/models';
import { newsApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getNewsFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import removeNullUndefined from 'src/utils/removeNullUndefined';
import { separateFilesAndPaths } from 'src/utils/separateFilesAndPaths';
import * as yup from 'yup';

interface INewsFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: INews;
}

export const NewsFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: INewsFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore, commonCodeStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;
    // 유형 select options
    const { newsSelectOptions } = commonCodeStore;
    const newsFormSchema = yup
      .object()
      .shape({
        prNewsCtgyCd: yup.string().required('카테고리를 선택해주세요.'),
        title: yup.string().required('제목을 입력해주세요.'),
      })
      .nullable();

    const [files, setFiles] = useState<Array<File | string>>([]);

    useEffect(() => {
      setFiles(selectedData?.filePath || []);
    }, []);

    const methods = useForm<TNewsFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      resolver: yupResolver(newsFormSchema),
      defaultValues: getNewsFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TNewsFormData) => {
      // files의 upload파일과 기존이미지path 추출
      const { files: uploadFiles, paths: existingPaths } = separateFilesAndPaths(files || []);

      // 삭제할이미지path 추출
      const originalPaths = selectedData?.filePath || [];
      const deleteFilesPath = originalPaths.filter((path) => !existingPaths.includes(path));

      const formData: TNewsFormData = {
        // 공통
        ...(uploadFiles.length > 0 && { files: uploadFiles }),
        ...removeNullUndefined<TNewsFormData>(data),
        // 수정일 경우
        ...(isCreate
          ? {}
          : {
              newsSid: selectedData?.newsSid || 0,
              ...(existingPaths.length > 0 && { filePath: existingPaths }),
              ...(deleteFilesPath.length > 0 && { deleteFilesPath }),
            }),
      };

      const responseText = isCreate ? '등록' : '수정';
      const result = isCreate ? newsApi.post(formData) : newsApi.put(formData);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedData?.newsSid) return;
        newsApi
          .remove(selectedData?.newsSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '뉴스 삭제',
        content: '해당 뉴스을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };
    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 2fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('news.fields.category')}
                  name="prNewsCtgyCd"
                  options={newsSelectOptions}
                  useAll={false}
                />
              </FormItem>
              <FormItem>
                <RHFTextField label={t('news.form.source')} name="source" />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('news.form.link')} name="link" disabled={readonly} />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFDatePicker
                  label={t('news.form.originRegDt')}
                  name="originRegDt"
                  disabled={readonly}
                  value={selectedData?.originRegDt ? new Date(selectedData.originRegDt) : null}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.content')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('news.form.title')} name="title" disabled={readonly} />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <CEditor name={'descript'} placeholder={t('news.form.desc')} />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.image')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFUploadDynamic
                  name="filePath"
                  files={files}
                  onChange={(newFile) => setFiles(newFile)}
                  accept={{
                    'image/jpeg': [],
                    'image/jpg': [],
                    'image/png': [],
                    'application/pdf': [], // PDF 파일
                    'application/vnd.ms-excel': [], // Excel 파일 (.xls)
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // Excel 파일 (.xlsx)
                    'application/msword': [], // Word 파일 (.doc)
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // Word 파일 (.docx)
                  }}
                />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
